import { useState } from "react";
import { Icon } from "@iconify/react";
import ChartCard from "../../components/ChartCard";
import PlaceholderCard from "../../components/PlaceholderCard";
import StatTile from "../../components/StatTile";
import { EmptyChartNote } from "../../components/charts/StackedColumns";
import TrendLine from "../../components/charts/TrendLine";
import { CardGrid, NoteCard, Section } from "../../components/Layout";
import { formatDayTick, formatNumber } from "../../format";
import { mixpanelLink } from "../../mixpanel";
import { colors, type, viz } from "../../styles";
import { EngagementStatsResponse } from "../../types";

/**
 * Products with submissions/tickets, reconstructed from `events/{id}/tickets`.
 *
 * Called "products", not "events": everything under Markit's `events`
 * collection counts except third-party-ticketed Eventbrite/Showclix imports
 * (a product decision, not a measured one) — so this includes posts, webinars,
 * and video/audio/PDF/custom product submissions/tickets, not just ticketed
 * gatherings.
 *
 * The threshold is 20 and inclusive, taken from the Mixpanel metric being
 * replaced (`Events With 20 Attendees`) — deliberately NOT the legacy
 * AdminDashboardEvents screen's `> 10`, which despite its "Min. 10" label
 * actually meant 11.
 *
 * A "submission/ticket" is a claimed, accepted, non-organizer ticket — NOT
 * `scanned`. `TicketV2.scanned` is the door check-in flag and looks like the
 * obvious choice, but measurement killed it: across a 120-event sample only
 * 1.44% of tickets had ever been scanned, and just 5 of those events reached
 * 20 scanned. A card built on it would report near-zero forever. The full
 * reasoning is in the backend README.
 */
const ATTENDED_SERIES = [
  { key: "eventsAttended", label: "Products with ≥1 submission/ticket", color: viz.series1 },
];

const WITH_20_SERIES = [
  { key: "eventsWith20", label: "Products with 20+ submissions/tickets", color: viz.series7 },
];

const HealthSection = ({
  engagement,
  engagementError,
  dimmed,
}: {
  engagement: EngagementStatsResponse | undefined;
  engagementError: string | undefined;
  dimmed: boolean;
}) => {
  const [expanded, setExpanded] = useState(false);

  const syncedAt = engagement?.computedAt ?? undefined;
  // Days outside the rollup's event window come back as null — genuinely
  // unknown, so they are dropped from the series rather than drawn as zero.
  const eventDays = (engagement?.days ?? []).filter(
    day => day.eventsAttended !== null
  );
  const totalAttended = eventDays.reduce(
    (sum, day) => sum + (day.eventsAttended ?? 0),
    0
  );
  const totalWith20 = eventDays.reduce(
    (sum, day) => sum + (day.eventsWith20 ?? 0),
    0
  );
  const unknownDays = (engagement?.days ?? []).length - eventDays.length;

  const emptyLabel =
    engagementError ??
    (eventDays.length === 0
      ? "The nightly rollup has not written event attendance yet."
      : undefined);

  return (
    <Section title="Health" description="Errors and event-side signals.">
      <CardGrid min={280}>
        <PlaceholderCard
          title="Webapp RSVP failures"
          source="Mixpanel · Error Monitoring · Webapp RSVP Failure Events (report 91174219)"
          shape="line"
          height={110}
          link={mixpanelLink.insights("91174219")}
          linkLabel="Open in Mixpanel"
        />
      </CardGrid>

      <button
        onClick={() => setExpanded(current => !current)}
        aria-expanded={expanded}
        style={{
          ...type.bodySmallMedium,
          color: colors.inkMuted,
          background: "transparent",
          border: "none",
          padding: "12px 0 0",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <Icon icon={expanded ? "ion:chevron-down" : "ion:chevron-forward"} height={13} />
        More metrics
      </button>

      {expanded && (
        <div style={{ marginTop: 12 }}>
          <CardGrid min={220}>
            <StatTile
              label="Products with submissions/tickets (range)"
              value={emptyLabel ? "—" : formatNumber(totalAttended)}
              secondary="Events, posts, and products with at least one submission/ticket"
              tone={engagementError ? "warning" : "neutral"}
              freshness={{ kind: "cached", updatedAt: syncedAt }}
            />
            <StatTile
              label="Products with 20+ submissions/tickets"
              value={emptyLabel ? "—" : formatNumber(totalWith20)}
              secondary={`Threshold ${
                engagement?.attendeeThreshold ?? 20
              }, inclusive`}
              tone={engagementError ? "warning" : "neutral"}
              freshness={{ kind: "cached", updatedAt: syncedAt }}
            />
          </CardGrid>

          <div style={{ marginTop: 16 }}>
            <CardGrid min={280}>
              <ChartCard
                title="Products with submissions/tickets, by day"
                subtitle="Nightly rollup · products bucketed by their own start day"
                series={ATTENDED_SERIES}
                dimmed={dimmed}
                freshness={{ kind: "cached", updatedAt: syncedAt }}
                table={{
                  categoryLabel: "Day",
                  rows: eventDays.map(day => ({
                    category: day.date,
                    values: { eventsAttended: day.eventsAttended ?? 0 },
                  })),
                }}
              >
                {emptyLabel ? (
                  <EmptyChartNote label={emptyLabel} />
                ) : (
                  <TrendLine
                    data={eventDays}
                    series={ATTENDED_SERIES}
                    categoryKey="date"
                    height={110}
                    tickFormatter={formatDayTick}
                    labelFormatter={formatDayTick}
                  />
                )}
              </ChartCard>

              <ChartCard
                title="Products with 20+ submissions/tickets"
                subtitle="Nightly rollup · claimed accepted tickets, excluding organizers"
                series={WITH_20_SERIES}
                dimmed={dimmed}
                freshness={{ kind: "cached", updatedAt: syncedAt }}
                table={{
                  categoryLabel: "Day",
                  rows: eventDays.map(day => ({
                    category: day.date,
                    values: { eventsWith20: day.eventsWith20 ?? 0 },
                  })),
                }}
              >
                {emptyLabel ? (
                  <EmptyChartNote label={emptyLabel} />
                ) : (
                  <TrendLine
                    data={eventDays}
                    series={WITH_20_SERIES}
                    categoryKey="date"
                    height={110}
                    tickFormatter={formatDayTick}
                    labelFormatter={formatDayTick}
                  />
                )}
              </ChartCard>
            </CardGrid>
          </div>

          <div style={{ marginTop: 16 }}>
            <NoteCard title="“Submission/ticket” here means a ticket with an owner, not a door scan">
              A submission/ticket is a ticket with an owner, accepted, minus the
              host&rsquo;s own organizer tickets. It is deliberately{" "}
              <strong>not</strong>{" "}
              <code>scanned</code>: only 1.4% of Markit tickets are ever scanned at
              the door, so a scan-based count would report almost nothing and read
              as though nobody attends. This covers every product type — posts,
              webinars, and video/audio/PDF/custom product submissions/tickets,
              not just ticketed events — except Eventbrite and Showclix imports,
              which are excluded; test-account events are excluded too.
              {unknownDays > 0 && (
                <>
                  {" "}
                  {unknownDays} day{unknownDays === 1 ? "" : "s"} in this range
                  {unknownDays === 1 ? " falls" : " fall"} outside the
                  rollup&rsquo;s event window and{" "}
                  {unknownDays === 1 ? "is" : "are"} omitted
                  rather than drawn as zero.
                </>
              )}
            </NoteCard>
          </div>
        </div>
      )}
    </Section>
  );
};

export default HealthSection;
